import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// layouts
import { DashboardLayoutWithSideNav, DashboardLayoutWithoutSideNav } from '../../app/layouts/dashboard';
import LogoOnlyLayout from '../../app/layouts/LogoOnlyLayout';
// guards
import { AuthGuard, GuestGuard, BankingDetailsGuard } from '../../app/guards';
// components
import { LoadingScreen } from '../components/base';
// paths
import {
  PATH_CUSTOMER,
  PATH_PROVIDER,
  PATH_HOME,
  PATH_MESSAGES,
  PATH_SERVICES,
  PATH_JOB_BOARD,
  PATH_EXTERNAL_REVIEWS,
} from './paths';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: 'auth',
      element: <DashboardLayoutWithoutSideNav />,
      children: [
        {
          path: 'landing',
          element: (
            <GuestGuard>
              <Landing />
            </GuestGuard>
          ),
        },
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: 'sign-up',
          element: (
            <GuestGuard>
              <SignUp />
            </GuestGuard>
          ),
        },
        {
          path: 'check-email',
          element: (
            <GuestGuard>
              <CheckEmailPage />
            </GuestGuard>
          ),
        },

        { path: 'login-unprotected', element: <Login /> },
        { path: 'reset-password', element: <ResetPassword /> },
      ],
    },

    // Customer Routes
    {
      path: 'customer',
      element: (
        <AuthGuard>
          <DashboardLayoutWithSideNav />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH_CUSTOMER.dashboard} replace />, index: true },
        { path: 'dashboard', element: <CustomerDashboard /> },
        { path: 'profile', element: <CustomerProfile /> },
        { path: 'bookings', element: <CustomerBookingsList /> },
        { path: 'bookings/:id/details', element: <CustomerBookingDetails /> },
        { path: 'bookings/:bookingId/payment-complete', element: <PaymentCompleteCallback /> },
        { path: 'jobs', element: <JobsPosted /> },
        { path: 'jobs/create', element: <CreateJobOffering /> },
        { path: 'jobs/:id/edit', element: <EditJobOffering /> },
        { path: 'reviews', element: <CustomerReviews /> },
        { path: 'favourites', element: <Favourites /> },
      ],
    },

    // Provider Routes
    {
      path: 'provider',
      element: (
        <AuthGuard>
          <DashboardLayoutWithSideNav />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH_PROVIDER.dashboard} replace />, index: true },
        { path: 'dashboard', element: <ProviderDashboard /> },
        { path: 'profile', element: <ProviderProfile /> },
        { path: 'preview-profile', element: <PreviewProfilePage /> },
        { path: 'reviews', element: <ProviderReviews /> },
        { path: 'banking-details', element: <BankingDetails /> },
        { path: 'manage-subscription', element: <ManageSubscription /> },
        {
          path: 'listings',
          element: (
            <BankingDetailsGuard>
              <ProviderListings />
            </BankingDetailsGuard>
          ),
        },
        {
          path: 'listings/create',
          element: (
            <BankingDetailsGuard>
              <ProviderCreateListing />
            </BankingDetailsGuard>
          ),
        },
        {
          path: 'listings/:id/edit',
          element: (
            <BankingDetailsGuard>
              <ProviderEditListing />
            </BankingDetailsGuard>
          ),
        },
        {
          path: 'listings/preview',
          element: (
            <BankingDetailsGuard>
              <PreviewListing />
            </BankingDetailsGuard>
          ),
        },
        {
          path: 'calendar',
          element: (
            <BankingDetailsGuard>
              <MyCalendar />
            </BankingDetailsGuard>
          ),
        },
        {
          path: 'bookings',
          element: (
            <BankingDetailsGuard>
              <ProviderBookingsList />
            </BankingDetailsGuard>
          ),
        },
        {
          path: 'bookings/:id/details',
          element: (
            <BankingDetailsGuard>
              <ProviderBookingDetails />
            </BankingDetailsGuard>
          ),
        },
        {
          path: 'quotes',
          element: (
            <BankingDetailsGuard>
              <MyQuotesList />
            </BankingDetailsGuard>
          ),
        },
        {
          path: 'quotes/create',
          element: (
            <BankingDetailsGuard>
              <CreateQuote />
            </BankingDetailsGuard>
          ),
        },
      ],
    },

    // Home Routes
    {
      path: PATH_HOME.root,
      element: <DashboardLayoutWithoutSideNav />,
      children: [{ element: <HomePage />, index: true }],
    },

    // Quotes Routes
    {
      path: 'quotes',
      element: <DashboardLayoutWithSideNav />,
      children: [
        { element: <MessagesPage />, index: true },
        { path: ':id/details', element: <QuoteDetails /> },
      ],
    },

    // Messages Routes
    {
      path: PATH_MESSAGES.root,
      element: <DashboardLayoutWithoutSideNav />,
      children: [
        { element: <MessagesPage />, index: true },
        { path: 'profile', element: <ContactProfilePage /> },
      ],
    },

    // Job Board Routes
    {
      path: PATH_JOB_BOARD.root,
      element: <DashboardLayoutWithoutSideNav />,
      children: [
        { element: <JobBoardPage />, index: true },
        { path: ':id/details', element: <JobDetails /> },
      ],
    },

    // Services Routes
    {
      path: PATH_SERVICES.root,
      element: <DashboardLayoutWithoutSideNav />,
      children: [
        { element: <Navigate to="/services" replace />, index: true },
        { path: ':id/details', element: <ServiceDetails /> },
        { path: 'list', element: <ServicesList /> },
      ],
    },

    // External Reviews
    {
      path: PATH_EXTERNAL_REVIEWS.root,
      element: <DashboardLayoutWithoutSideNav />,
      children: [
        { element: <ExternalReviews />, index: true },
        { path: ':listingId', element: <ExternalReviews /> },
      ],
    },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '500', element: <Page500 /> },
        { path: '404', element: <Page404 /> },
        { path: '403', element: <Page403 /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    {
      path: '/',
      element: (
        <AuthGuard>
          <DashboardLayoutWithSideNav />
        </AuthGuard>
      ),
      children: [{ element: <CustomerDashboard />, index: true }],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// AUTHENTICATION
const Landing = Loadable(lazy(() => import('../../modules/auth/Landing')));
const Login = Loadable(lazy(() => import('../../modules/auth/Login')));
const ResetPassword = Loadable(lazy(() => import('../../modules/auth/ResetPassword')));
const SignUp = Loadable(lazy(() => import('../../modules/auth/SignUp')));
const CheckEmailPage = Loadable(lazy(() => import('../../modules/auth/SignUp/CheckEmailPage')));

// CUSTOMER
const CustomerDashboard = Loadable(lazy(() => import('../../modules/customer/CustomerDashboard')));
const CustomerProfile = Loadable(lazy(() => import('../../modules/customer/CustomerProfile')));
const JobsPosted = Loadable(lazy(() => import('../../modules/customer/JobsPosted')));
const CreateJobOffering = Loadable(lazy(() => import('../../modules/customer/CreateJobOffering')));
const EditJobOffering = Loadable(lazy(() => import('../../modules/customer/EditJobOffering')));
const CustomerBookingsList = Loadable(lazy(() => import('../../modules/customer/MyBookings/CustomerBookingsList')));
const CustomerBookingDetails = Loadable(lazy(() => import('../../modules/customer/MyBookings/CustomerBookingDetails')));
const PaymentCompleteCallback = Loadable(
  lazy(() => import('../../modules/customer/MyBookings/PaymentCompleteCallback'))
);
const CustomerReviews = Loadable(lazy(() => import('../../modules/customer/Reviews')));
const Favourites = Loadable(lazy(() => import('../../modules/customer/Favourites')));

// PROVIDER
const ProviderDashboard = Loadable(lazy(() => import('../../modules/provider/ProviderDashboard')));
const ProviderProfile = Loadable(lazy(() => import('../../modules/provider/ProviderProfile')));
const PreviewProfilePage = Loadable(lazy(() => import('../../modules/provider/PreviewProfilePage')));
const ProviderListings = Loadable(lazy(() => import('../../modules/provider/MyListings')));
const ProviderCreateListing = Loadable(lazy(() => import('../../modules/provider/CreateListing')));
const ProviderEditListing = Loadable(lazy(() => import('../../modules/provider/EditListing')));
const PreviewListing = Loadable(lazy(() => import('../../modules/provider/PreviewListing')));
const MyCalendar = Loadable(lazy(() => import('../../modules/provider/MyCalendar')));
const MyQuotesList = Loadable(lazy(() => import('../../modules/provider/MyQuotes')));
const CreateQuote = Loadable(lazy(() => import('../../modules/provider/CreateQuote')));
const ProviderBookingsList = Loadable(lazy(() => import('../../modules/provider/MyBookings/ProviderBookingsList')));
const ProviderBookingDetails = Loadable(lazy(() => import('../../modules/provider/MyBookings/ProviderBookingDetails')));
const BankingDetails = Loadable(lazy(() => import('../../modules/user/BankingDetails/BankingDetailsPage')));
const ProviderReviews = Loadable(lazy(() => import('../../modules/provider/Reviews')));
const ManageSubscription = Loadable(lazy(() => import('../../modules/provider/ManageSubscription')));

// HOME
const HomePage = Loadable(lazy(() => import('../../modules/home')));

// MESSAGES
const MessagesPage = Loadable(lazy(() => import('../../modules/messages/MessagesPage')));
const ContactProfilePage = Loadable(lazy(() => import('../../modules/messages/ContactProfilePage')));

// JOB BOARD
const JobBoardPage = Loadable(lazy(() => import('../../modules/job-board/JobBoardPage')));
const JobDetails = Loadable(lazy(() => import('../../modules/job-board/JobDetails')));

// SERVICES
const ServicesList = Loadable(lazy(() => import('../../modules/services/ServicesList')));
const ServiceDetails = Loadable(lazy(() => import('../../modules/services/ServicesDetails')));

// QUOTES
const QuoteDetails = Loadable(lazy(() => import('../../modules/quotes/QuoteDetails')));

// EXTERNAL REVIEWS
const ExternalReviews = Loadable(lazy(() => import('../../modules/ExternalReviews')));

// MAIN
const Page500 = Loadable(lazy(() => import('../../modules/auth/Page500')));
const Page403 = Loadable(lazy(() => import('../../modules/auth/Page403')));
const Page404 = Loadable(lazy(() => import('../../modules/auth/Page404')));
